import React, { createContext, useContext, useState } from "react";

// ─── Date Utilities ──────────────────────────────────────────────────────────

export function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function formatDisplayDate(value) {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[2]}.${match[3]}.${match[1]}` : '';
}

function subDays(date, days) {
  const result = new Date(date);
  result.setDate(result.getDate() - days);
  return result;
}

export function getPresetPeriods(preset) {
  const today = new Date();
  const f = formatDate;

  switch (preset) {
    case "today_vs_yesterday": {
      const yesterday = subDays(today, 1);
      return {
        a: { start: f(yesterday), end: f(yesterday) },
        b: { start: f(today),     end: f(today) },
      };
    }
    case "this_week_vs_last_week": {
      const dow = today.getDay(); // 0 = Sun
      const startThisWeek = subDays(today, dow);
      const endLastWeek   = subDays(startThisWeek, 1);
      const startLastWeek = subDays(endLastWeek, 6);
      return {
        a: { start: f(startLastWeek), end: f(endLastWeek) },
        b: { start: f(startThisWeek), end: f(today) },
      };
    }
    case "this_month_vs_last_month": {
      const startThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const endLastMonth   = subDays(startThisMonth, 1);
      const startLastMonth = new Date(endLastMonth.getFullYear(), endLastMonth.getMonth(), 1);
      return {
        a: { start: f(startLastMonth), end: f(endLastMonth) },
        b: { start: f(startThisMonth), end: f(today) },
      };
    }
    case "this_quarter_vs_last_quarter": {
      const currentQ      = Math.floor(today.getMonth() / 3);
      const startThisQ    = new Date(today.getFullYear(), currentQ * 3, 1);
      const endLastQ      = subDays(startThisQ, 1);
      const lastQStart    = Math.floor(endLastQ.getMonth() / 3) * 3;
      const startLastQ    = new Date(endLastQ.getFullYear(), lastQStart, 1);
      return {
        a: { start: f(startLastQ), end: f(endLastQ) },
        b: { start: f(startThisQ), end: f(today) },
      };
    }
    case "this_year_vs_last_year": {
      const startThisYear = new Date(today.getFullYear(), 0, 1);
      const endLastYear   = new Date(today.getFullYear() - 1, 11, 31);
      const startLastYear = new Date(today.getFullYear() - 1, 0, 1);
      return {
        a: { start: f(startLastYear), end: f(endLastYear) },
        b: { start: f(startThisYear), end: f(today) },
      };
    }
    default:
      return null; // custom — keep whatever is in state
  }
}

// ─── Defaults ────────────────────────────────────────────────────────────────

const defaultFilters = () => ({
  startDate: formatDate(subDays(new Date(), 30)),
  endDate:   formatDate(new Date()),
});

const defaultComparison = () => {
  const periods = getPresetPeriods("today_vs_yesterday");
  return {
    preset:  "today_vs_yesterday",
    periodA: periods.a,   // { start, end }
    periodB: periods.b,
    // per-period UI mode: "single" (one date) or "range" (start + end)
    modeA: "single",
    modeB: "single",
  };
};

// ─── Context ─────────────────────────────────────────────────────────────────

const FilterContext = createContext();

export function FilterProvider({ children }) {
  const [filters,     setFilters]     = useState(defaultFilters);
  const [compareMode, setCompareMode] = useState(false);
  const [comparison,  setComparison]  = useState(defaultComparison);

  // When a preset is selected, auto-calculate the periods
  const applyPreset = (preset) => {
    if (preset === "custom") {
      setComparison((prev) => ({ ...prev, preset: "custom" }));
      return;
    }
    const periods = getPresetPeriods(preset);
    setComparison((prev) => ({
      ...prev,
      preset,
      periodA: periods.a,
      periodB: periods.b,
      modeA: periods.a.start === periods.a.end ? "single" : "range",
      modeB: periods.b.start === periods.b.end ? "single" : "range",
    }));
  };

  // Update a single period (A or B)
  const updatePeriod = (which, field, value) => {
    const key = which === "a" ? "periodA" : "periodB";
    setComparison((prev) => ({
      ...prev,
      preset: "custom",
      [key]: { ...prev[key], [field]: value },
    }));
  };

  // Switch between single-date and range mode for a period
  const setPeriodMode = (which, mode) => {
    const modeKey = which === "a" ? "modeA" : "modeB";
    const periodKey = which === "a" ? "periodA" : "periodB";
    setComparison((prev) => {
      const period = prev[periodKey];
      // When switching to single, collapse end to equal start
      const updated = mode === "single"
        ? { start: period.start, end: period.start }
        : period;
      return { ...prev, preset: "custom", [modeKey]: mode, [periodKey]: updated };
    });
  };

  // Normal filter helpers
  const updateFilter   = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const updateDateRange = (startDate, endDate) => setFilters((prev) => ({ ...prev, startDate, endDate }));
  const resetFilters   = () => setFilters(defaultFilters());

  return (
    <FilterContext.Provider value={{
      filters,
      updateFilter,
      updateDateRange,
      resetFilters,
      compareMode,
      setCompareMode,
      comparison,
      applyPreset,
      updatePeriod,
      setPeriodMode,
    }}>
      {children}
    </FilterContext.Provider>
  );
}

export const useFilters = () => {
  const ctx = useContext(FilterContext);
  if (!ctx) throw new Error("useFilters must be used within a FilterProvider");
  return ctx;
};
